/**
 * Decoder del scene binario .epcb v1. Contraparte de binary-scene.encoder.js.
 *
 * Diseño zero-copy: cada polyline devuelve un `Float32Array` que es una
 * vista sobre el ArrayBuffer original (no copia). Esto significa que armar
 * un `THREE.BufferGeometry` con `setAttribute('position', new
 * BufferAttribute(decoded.vertices, 2))` no aloca memoria extra ni recorre
 * los datos. Para que la vista funcione, el offset al inicio de cada bloque
 * de vértices DEBE estar alineado a 4 bytes — el encoder ya lo garantiza.
 *
 * El decoder valida magic + version y lanza si algo no cuadra; eso es señal
 * de archivo corrupto o producido por una versión incompatible.
 */

const MAGIC      = 'EPCB'
const VERSION    = 1
const HEADER_LEN = 24

/**
 * @param {ArrayBuffer} buffer
 * @returns {{
 *   version: number,
 *   totalVerts: number,
 *   totalPolys: number,
 *   layers: Array<{
 *     code: string,
 *     color: number,
 *     polylines: Array<{
 *       vertices: Float32Array,   // view zero-copy [x0,y0,x1,y1,...]
 *       vertCount: number,
 *       width: number,
 *       closed: boolean,
 *     }>
 *   }>
 * }}
 */
export function decodeScene(buffer) {
  if (!(buffer instanceof ArrayBuffer)) {
    throw new Error('decodeScene: se esperaba ArrayBuffer')
  }
  if (buffer.byteLength < HEADER_LEN) {
    throw new Error('decodeScene: buffer demasiado corto')
  }

  const view = new DataView(buffer)
  const u8   = new Uint8Array(buffer)

  // Validación de magic 'EPCB'
  const magic = String.fromCharCode(u8[0], u8[1], u8[2], u8[3])
  if (magic !== MAGIC) {
    throw new Error(`decodeScene: magic inválido '${magic}' (esperaba '${MAGIC}')`)
  }

  let off = 4
  const version    = view.getUint32(off, true); off += 4
  if (version !== VERSION) {
    throw new Error(`decodeScene: versión no soportada ${version} (esperaba ${VERSION})`)
  }
  const layerCount = view.getUint32(off, true); off += 4
  const totalVerts = view.getUint32(off, true); off += 4
  const totalPolys = view.getUint32(off, true); off += 4
  off += 4 // reserved

  const decoder = new TextDecoder()
  const layers  = new Array(layerCount)

  for (let i = 0; i < layerCount; i++) {
    const codeLen = view.getUint16(off, true); off += 2
    const code    = decoder.decode(new Uint8Array(buffer, off, codeLen))
    off += codeLen
    const color     = view.getUint32(off, true); off += 4
    const polyCount = view.getUint32(off, true); off += 4

    const polylines = new Array(polyCount)
    for (let j = 0; j < polyCount; j++) {
      const vertCount  = view.getUint32(off, true);  off += 4
      const width      = view.getFloat32(off, true); off += 4
      const closedFlag = view.getUint8(off);         off += 1
      // Padding hasta múltiplo de 4 — mismo cálculo que en el encoder.
      off += (4 - (off % 4)) % 4

      // Zero-copy view sobre el buffer original.
      const vertices = new Float32Array(buffer, off, vertCount * 2)
      off += vertCount * 2 * 4

      polylines[j] = { vertices, vertCount, width, closed: !!closedFlag }
    }

    layers[i] = { code, color, polylines }
  }

  return { version, totalVerts, totalPolys, layers }
}
